import { Link } from "react-router-dom";
import { BookOpen, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const Section = ({ title, children }) => (
  <section className="space-y-2">
    <h2 className="font-heading font-semibold text-lg text-foreground">{title}</h2>
    <div className="text-sm leading-relaxed text-muted-foreground space-y-2">{children}</div>
  </section>
);

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/50">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/auth" className="flex items-center gap-2.5 text-primary font-heading font-bold text-xl hover:opacity-80 transition-opacity">
            <BookOpen className="h-6 w-6" />
            <span>EduGrade</span>
          </Link>
          <Link to="/auth">
            <Button variant="ghost" size="sm" data-testid="privacy-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8 animate-fade-in">
        <div>
          <h1 className="font-heading font-bold text-3xl tracking-tight">Política de Privacidade</h1>
          <p className="text-muted-foreground text-sm mt-2">Última atualização: junho de 2026</p>
        </div>

        <p className="text-sm leading-relaxed text-muted-foreground">
          O EduGrade ("a plataforma", "nós") é um serviço de correção de testes com apoio de inteligência
          artificial, disponível em <span className="text-foreground font-medium">ia-nas-escolas.org</span>.
          Esta política explica que dados tratamos, para quê, e quais os seus direitos.
        </p>

        <Section title="1. Responsável pelo tratamento">
          <p>
            A responsável pelo tratamento dos dados da plataforma é <strong className="text-foreground">Sandra Lourenço</strong>,
            que pode ser contactada através de{" "}
            <a href="mailto:sandracplourenco@gmail.com" className="text-primary hover:underline inline-flex items-center gap-1">
              <Mail className="h-3.5 w-3.5" /> sandracplourenco@gmail.com
            </a>.
          </p>
        </Section>

        <Section title="2. Que dados tratamos">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-foreground">Dados da conta do professor:</strong> endereço de email e palavra-passe (guardada de forma cifrada); nome, se for fornecido.</li>
            <li><strong className="text-foreground">Conteúdos carregados pelo professor:</strong> enunciados, corrigendas e as imagens ou ficheiros PDF dos testes dos alunos, bem como as cotações, classificações, nomes e observações que o professor introduza. Estes conteúdos podem conter dados pessoais de alunos, incluindo menores.</li>
            <li><strong className="text-foreground">Dados técnicos de funcionamento:</strong> registos de acesso e endereço IP, usados para segurança e funcionamento do serviço.</li>
          </ul>
        </Section>

        <Section title="3. Finalidades e fundamento">
          <p>Tratamos os dados para:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>prestar o serviço de correção e gerar os mapas de avaliação e as fichas de resultados;</li>
            <li>criar, autenticar e gerir a conta do professor;</li>
            <li>garantir a segurança e o bom funcionamento da plataforma.</li>
          </ul>
          <p>
            O fundamento é a execução do serviço que o professor solicita.{" "}
            <strong className="text-foreground">Não usamos os dados para publicidade nem os vendemos a terceiros.</strong>
          </p>
        </Section>

        <Section title="4. Papéis e responsabilidades">
          <p>
            Quanto aos <strong className="text-foreground">dados dos alunos</strong>, o{" "}
            <strong className="text-foreground">professor e a respetiva escola ou agrupamento</strong> são os responsáveis
            pelo tratamento: são eles que decidem carregar os testes e que devem assegurar a base legal para o fazer, de
            acordo com as regras da sua instituição. O EduGrade atua como <strong className="text-foreground">subcontratante</strong>,
            tratando esses dados apenas para prestar o serviço pedido pelo professor.
          </p>
        </Section>

        <Section title="5. Subcontratantes e partilha de dados">
          <p>Para funcionar, o EduGrade recorre a:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-foreground">Alojamento da aplicação:</strong> Emergent (infraestrutura de alojamento em nuvem).</li>
            <li><strong className="text-foreground">Processamento de IA para a correção:</strong> OpenAI, que processa as imagens dos testes nos Estados Unidos. Este fornecedor <strong className="text-foreground">não utiliza</strong> os conteúdos para treinar os seus modelos.</li>
          </ul>
          <p>
            Estes prestadores atuam como subcontratantes e tratam os dados apenas para os fins acima. Não partilhamos os
            dados com mais ninguém, salvo obrigação legal.
          </p>
          <p>
            As transferências internacionais de dados (por exemplo, para os Estados Unidos) são feitas ao abrigo das
            cláusulas contratuais-tipo da Comissão Europeia.
          </p>
        </Section>

        <Section title="6. Prazo de conservação">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><strong className="text-foreground">Dados da conta:</strong> enquanto a conta estiver ativa.</li>
            <li><strong className="text-foreground">Imagens e ficheiros dos testes:</strong> ficam guardados até o professor os eliminar. O professor pode eliminá-los a qualquer momento na plataforma.</li>
          </ul>
          <p>Quando deixam de ser necessários, os dados são eliminados ou anonimizados.</p>
        </Section>

        <Section title="7. Segurança">
          <p>
            Cada professor tem uma conta privada e só acede aos seus próprios testes. O acesso é feito através de ligação
            segura (HTTPS) e as palavras-passe são guardadas de forma cifrada.
          </p>
        </Section>

        <Section title="8. Dados de menores">
          <p>
            Os testes podem conter dados de alunos menores. Recomendamos que o professor carregue os testes de forma
            anonimizada sempre que possível e que siga as orientações de proteção de dados da sua escola. A responsabilidade
            pela recolha e pela base legal para tratar os dados dos alunos cabe ao professor e à instituição de ensino.
          </p>
        </Section>

        <Section title="9. Os seus direitos">
          <p>
            Ao abrigo do RGPD, tem direito a <strong className="text-foreground">aceder, retificar, apagar, limitar ou opor-se</strong> ao
            tratamento dos seus dados, bem como à <strong className="text-foreground">portabilidade</strong>. Para exercer estes direitos,
            contacte{" "}
            <a href="mailto:sandracplourenco@gmail.com" className="text-primary hover:underline">sandracplourenco@gmail.com</a>.
            Tem ainda o direito de apresentar reclamação à autoridade de controlo competente (em Portugal, a{" "}
            <strong className="text-foreground">CNPD</strong> —{" "}
            <a href="https://www.cnpd.pt" target="_blank" rel="noreferrer" className="text-primary hover:underline">www.cnpd.pt</a>).
          </p>
        </Section>

        <Section title="10. Cookies">
          <p>
            O EduGrade utiliza apenas o armazenamento local do navegador e os cookies estritamente necessários para
            autenticar a sessão e garantir o funcionamento do serviço. Não utiliza cookies de publicidade nem de rastreio.
          </p>
        </Section>

        <Section title="11. Alterações a esta política">
          <p>
            Esta política pode ser atualizada. A versão em vigor é a publicada nesta página, com a data de última
            atualização indicada no topo.
          </p>
        </Section>

        <Section title="12. Contacto">
          <p>
            Para qualquer questão sobre esta política ou sobre os seus dados, contacte{" "}
            <strong className="text-foreground">Sandra Lourenço</strong> através de{" "}
            <a href="mailto:sandracplourenco@gmail.com" className="text-primary hover:underline">sandracplourenco@gmail.com</a>.
          </p>
        </Section>

        <div className="pt-4 border-t border-border/50">
          <Link to="/auth">
            <Button variant="outline" size="sm" data-testid="privacy-back-bottom">
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar ao EduGrade
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
